import { ROOM_UNAVAILABLE_REASONS, type RoomUnavailableReason, SHORTLIST_INELIGIBLE_REASONS, type ShortlistIneligibleReason } from "@seagames/contracts";
import { i18n } from "../i18n";
import { ApiError } from "./client";

/**
 * What the operator reads when something fails.
 *
 * Every `ErrorCode` has an entry in the `errors` locale namespace
 * (`locales/<lng>/errors.json`), keyed by the code verbatim — that is what the
 * viewer reads, in their own language. A code the bundle does not know (a
 * newer API than this build) falls back to the server's English message, which
 * is always specific — never a generic "Something went wrong".
 */
export function errorMessage(error: unknown): string {
	if (error instanceof ApiError) {
		if (error.code === "RATE_LIMITED") return rateLimitMessage(error);
		const file =
			fileErrorMessage(error) ?? transportErrorMessage(error) ?? accommodationErrorMessage(error) ?? shortlistErrorMessage(error) ?? fnbErrorMessage(error) ?? entriesErrorMessage(error) ?? stageLockedMessage(error);
		if (file) return file;
		return i18n.t(`errors:${error.code}`, { defaultValue: error.message });
	}
	if (error instanceof Error) return error.message;
	return i18n.t("errors:GENERIC");
}

/**
 * The limiter counts failures only, so this is always the result of repeated
 * wrong attempts. Telling someone exactly how long to wait stops them hammering
 * the form and driving the counter further up. The seconds-vs-minutes branch
 * stays here; each branch is a plural key so every locale can decline it.
 */
function rateLimitMessage(error: ApiError): string {
	const seconds = Number(error.details?.["retry_after_seconds"] ?? 0);
	if (!Number.isFinite(seconds) || seconds <= 0) {
		return i18n.t("errors:RATE_LIMITED_noRetry");
	}
	if (seconds < 60) {
		return i18n.t("errors:RATE_LIMITED_waitSeconds", { count: Math.ceil(seconds) });
	}
	return i18n.t("errors:RATE_LIMITED_waitMinutes", { count: Math.ceil(seconds / 60) });
}

/**
 * The file codes (M4) whose `details` make the message actionable: the size
 * limit, the image rule that failed, the most files of a kind. Without the
 * detail (an older API) the plain code's message is used.
 */
function fileErrorMessage(error: ApiError): string | null {
	const d = error.details ?? {};
	const num = (k: string): number | null => {
		const v = Number(d[k]);
		return Number.isFinite(v) && v > 0 ? v : null;
	};
	switch (error.code) {
		case "FILE_TOO_LARGE": {
			const max = num("max_bytes");
			return max ? i18n.t("errors:FILE_TOO_LARGE_max", { mb: Math.floor(max / (1024 * 1024)) }) : null;
		}
		case "FILE_IMAGE_INVALID": {
			const reason = d["reason"];
			if (reason === "too_small") {
				const width = num("min_width");
				const height = num("min_height");
				return width && height ? i18n.t("errors:FILE_IMAGE_INVALID_too_small", { width, height }) : null;
			}
			if (reason === "aspect_ratio") return i18n.t("errors:FILE_IMAGE_INVALID_aspect_ratio");
			if (reason === "unreadable") return i18n.t("errors:FILE_IMAGE_INVALID_unreadable");
			return null;
		}
		case "FILE_LIMIT_REACHED": {
			const max = num("max");
			return max ? i18n.t("errors:FILE_LIMIT_REACHED_max", { count: max }) : null;
		}
		default:
			return null;
	}
}

/**
 * The transport codes (M10) whose `details` name what is in the way: the
 * trip that took the vehicle or driver first, the transfers the people are
 * already on, the place that already stands for a hotel. Refs are identifiers (`TRIP-000123`), listed as they are.
 * ALLOCATION_INVALID's per-row issues belong next to the rows, not in a toast.
 */
function transportErrorMessage(error: ApiError): string | null {
	const d = error.details ?? {};
	switch (error.code) {
		case "RESOURCE_BUSY": {
			const ref = d["ref"];
			return typeof ref === "string" && ref !== "" ? i18n.t("errors:RESOURCE_BUSY_ref", { ref }) : null;
		}
		case "TRANSFER_EXISTS": {
			const refs = Array.isArray(d["refs"]) ? d["refs"].filter((r): r is string => typeof r === "string" && r !== "") : [];
			return refs.length > 0 ? i18n.t("errors:TRANSFER_EXISTS_refs", { refs: refs.join(", ") }) : null;
		}
		case "HOTEL_ALREADY_LINKED": {
			const place = d["place_name"];
			return typeof place === "string" && place !== "" ? i18n.t("errors:HOTEL_ALREADY_LINKED_place", { place }) : null;
		}
		default:
			return null;
	}
}

/**
 * The accommodation code (M11) whose `details.reason` says WHY the room
 * cannot take the person: reserved for another contingent, out of service,
 * the other sex, or not enough beds. An unknown reason (a newer API) falls
 * back to the plain code's message.
 */
function accommodationErrorMessage(error: ApiError): string | null {
	if (error.code !== "ROOM_NOT_AVAILABLE") return null;
	const reason = error.details?.["reason"];
	const known = (ROOM_UNAVAILABLE_REASONS as readonly unknown[]).includes(reason);
	return known ? i18n.t(`errors:ROOM_NOT_AVAILABLE_${reason as RoomUnavailableReason}`) : null;
}

/**
 * The shortlist codes (M7a) whose `details` say more: the event's limit and
 * how many primaries are already picked; why the card cannot be shortlisted
 * (SHORTLIST_INELIGIBLE_REASONS). Without them, the plain code's message.
 */
function shortlistErrorMessage(error: ApiError): string | null {
	const d = error.details ?? {};
	switch (error.code) {
		case "SHORTLIST_QUOTA_FULL": {
			const limit = Number(d["limit"]);
			const used = Number(d["used"]);
			return Number.isFinite(limit) && Number.isFinite(used) && d["limit"] !== undefined && d["used"] !== undefined
				? i18n.t("errors:SHORTLIST_QUOTA_FULL_limit", { limit, used })
				: null;
		}
		case "SHORTLIST_NOT_ELIGIBLE": {
			const reason = d["reason"];
			const known = (SHORTLIST_INELIGIBLE_REASONS as readonly unknown[]).includes(reason);
			return known ? i18n.t(`errors:SHORTLIST_NOT_ELIGIBLE_${reason as ShortlistIneligibleReason}`) : null;
		}
		default:
			return null;
	}
}

/**
 * The F&B codes (M12) whose `details` name what is in the way: the hall the
 * building already eats at, the time the meal started (`HH:MM`, the games'
 * wall clock — the hall's timetable, not a viewer-local instant). Without
 * them, the plain code's message. HALL_CLUSTER_MISMATCH's building ids and
 * CARD_NOT_FOUND need no detail.
 */
function fnbErrorMessage(error: ApiError): string | null {
	const d = error.details ?? {};
	switch (error.code) {
		case "BUILDING_HAS_HALL": {
			const hall = d["dining_hall_name"];
			return typeof hall === "string" && hall !== "" ? i18n.t("errors:BUILDING_HAS_HALL_hall", { hall }) : null;
		}
		case "TAKEAWAY_MEAL_STARTED": {
			const time = d["meal_starts_at"];
			return typeof time === "string" && time !== "" ? i18n.t("errors:TAKEAWAY_MEAL_STARTED_at", { time }) : null;
		}
		default:
			return null;
	}
}

/**
 * The sport entries codes (M7b) whose `details` say more: an event's cap per
 * NOC. The other entry codes read fine on their own.
 */
/**
 * M7c: a page or action the NOC's stage has not opened yet. Says which stage
 * opens it when the API names it (`details.opens_at_stage`). Never a
 * sign-out — the shell also swaps the page for the "not open yet" screen.
 */
function stageLockedMessage(error: ApiError): string | null {
	if (error.code !== "STAGE_LOCKED") return null;
	const n = Number(error.details?.["opens_at_stage"]);
	return Number.isInteger(n) && n > 0 ? i18n.t("errors:STAGE_LOCKED_opens", { n }) : null;
}

function entriesErrorMessage(error: ApiError): string | null {
	if (error.code !== "ENTRY_OVER_CAP") return null;
	const cap = Number(error.details?.["cap"]);
	return error.details?.["cap"] !== undefined && Number.isFinite(cap) ? i18n.t("errors:ENTRY_OVER_CAP_cap", { cap }) : null;
}

/** A transient failure keeps the session — the UI must not offer "sign in again". */
export const isTransientError = (error: unknown): boolean =>
	error instanceof ApiError && error.transient;

/**
 * The request ID to SHOW the user — which is not every request ID.
 *
 * It only earns screen space when an administrator could act on it: the ID is
 * the key that ties the failure to the server logs and to `audit_logs`, so it
 * is worth quoting when the fault is ours (a 5xx) or a dependency's (transient).
 * A wrong password, an expired code, a rate limit — those are already explained
 * by the message, there is nothing for support to look up, and a UUID under the
 * error just makes an ordinary typo look like a system fault.
 *
 * The API still returns `request_id` on EVERY failure and logs it regardless;
 * this only decides what is printed.
 */
export function supportRequestIdOf(error: unknown): string | undefined {
	if (!(error instanceof ApiError)) return undefined;
	const ourFault = error.status >= 500 || error.transient;
	return ourFault ? error.requestId : undefined;
}
